// @flow
/**
 * @author mkukreti
 * @since Mar 2018
 */
import * as React from 'react';
import classNames from '@ta/common.classnames';
import { connect } from 'react-redux';
import { ResponsiveTargetLink } from '@ta/common.routing';
import { Icon } from '@ta/styleguide.icon';
import { LazyImage } from '@ta/media.image';

import styles from './Avatar.less';

/** Avatar sizes */
export type Size = 'xtra-small' | 'small' | 'default' | 'large' | 'xtra-large';

// mapping of Size to avatar width
const sizeToWidth = {
  'xtra-small': 24,
  small: 32,
  default: 42,
  large: 72,
  'xtra-large': 120,
};

type PhotoSize = {|
  /** tripadvisor cdn url */
  url: string,
  /** width of the thumbnail */
  width: number,
  /** height of the thumbnail */
  height: number,
|};

// disable exact type so that consumers can use an inexact type as well
/**
 * Type for avatar photo, most commonly named 'avatar' in graphql feed
 */
export type AvatarPhoto = {|
  /** plain old id */
  id?: number,
  /** array of available photo sizes */
  photoSizes: PhotoSize[],
|};

type Display = 'inline';

type AvatarContentProps = {|
  /**
   * Image url to render the avatar for. If not provided, falls back on <span class="ui_icon member-fill"></span>
   *  It can be absolute (cdn mapped), relative(will be appended in the component with imgCdnUrl)
   * */
  photoUrl?: ?string,
  /** Provided by the global redux store. Don't need to be passed in props */
  imgCdnUrl?: string,
  /** Icon class to render with the avatar */
  icon?: ?string,
  /** Text to be shown if the img is missing */
  alt?: string,
  /** Controls whether we lazy load the avatar image or not */
  notLazy?: boolean,
|};

type LinkedAvatarProps = {|
  /** import all types from AvatarContentProps */
  ...AvatarContentProps,
  /** Link url for avatar click */
  href?: string,
  /** Target for avatar link. Default opens in a new window */
  target?: '_blank' | '_self',
  /** Click handler for avatar */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  /** Sets the display styles of the avatar. When true, the ui_social_avatar class is applied instead of ui_avatar. */
  withBorder?: boolean,
  /**  additional classname */
  className?: string,
  /** Target for a link avatar on mobile web. Defaults to the target property */
  mobileTarget?: '_blank' | '_self',
|};

/**
 * Type for props passed to the Avatar component
 */
export type AvatarProps = {|
  /** See LinkedAvatarProps */
  ...LinkedAvatarProps,
  /** Size of the avatar for mobile+ viewports. Default is 42px (if not provided), xtra-small, small, large, xtra-large */
  size?: Size,
  /** Size override for tablet+ viewports. Only works when withBorder=true. */
  tabletSize?: Size,
  /** Size override for desktop+ viewports. Only works when withBorder=true. */
  desktopSize?: Size,
  /** Size override fore widescreen viewports. Only works when withBorder=true. */
  widescreenSize?: Size,
  /** Block level of the element containing the img
   *  Use span and default to block for x-browser compatibility */
  display?: ?Display,
  /**
   * photo object to get avatar urls from. This will be used in favor of photoUrl if both are provided
   * If neither photo nor photoUrl are provided, Avatar falls back on <span class="ui_icon member-fill"></span>
   */
  photo?: AvatarPhoto,
|};

type AvatarInternalProps = {|
  ...AvatarProps,
  /** Which type of platform we are likely on based on viewport */
  viewportCategory: string,
|};

const DefaultProps = {
  className: undefined,
  photo: undefined,
  icon: null,
  size: 'default',
  tabletSize: undefined,
  desktopSize: undefined,
  widescreenSize: undefined,
  display: null,
  href: '',
  target: '_blank',
  mobileTarget: undefined,
  onClick: undefined,
  imgCdnUrl: '',
  alt: '',
  withBorder: false,
  notLazy: false,
};

const getCdnPhotoUrl = (mediaUrl: string, cdnUrl?: string) => {
  if (mediaUrl.indexOf('http://') === 0 || mediaUrl.indexOf('https://') === 0) {
    return mediaUrl;
  }
  return cdnUrl ? cdnUrl + mediaUrl : mediaUrl;
};

/**
 * Given a photo object, cdnUrl and size, return an image url or null if no
 * suitable photo size was found
 * @param photo
 * @param cdnUrl
 * @param size
 * @returns {string|null} image url for suitably sized avatar image
 */
const getPhotoUrlForSize = (photo: AvatarPhoto, size: Size = 'default'): ?string => {
  const minWidth = sizeToWidth[size];
  let url: ?string = null;
  if (photo && photo.photoSizes && photo.photoSizes.length > 0) {
    //
    const matchingPhotoSize = photo.photoSizes.find(photoSize =>
      photoSize.width >= minWidth ? photoSize.url : null,
    );
    // if no photoSize was suitably large, grab the last photoSize available
    url = matchingPhotoSize
      ? matchingPhotoSize.url
      : photo.photoSizes[photo.photoSizes.length - 1].url;
  }
  return url;
};

const getImageComponent = (src: string, alt: string, notLazy?: boolean): React.Node =>
  notLazy ? <img src={src} alt={alt} /> : <LazyImage src={src} alt={alt} />;

const AvatarContent = ({ photoUrl, imgCdnUrl, icon, alt, notLazy }: AvatarContentProps) => (
  <React.Fragment>
    {photoUrl ? (
      getImageComponent(getCdnPhotoUrl(photoUrl, imgCdnUrl), alt || '', notLazy)
    ) : (
      <Icon name="member-fill" />
    )}
    {icon && <div className={icon} />}
  </React.Fragment>
);

const LinkedAvatar = (props: LinkedAvatarProps) => (
  <ResponsiveTargetLink
    className={props.className}
    href={props.href}
    target={props.target}
    mobileTarget={props.mobileTarget}
    onClick={props.onClick}
  >
    <AvatarContent
      photoUrl={props.photoUrl}
      imgCdnUrl={props.imgCdnUrl}
      icon={props.icon}
      alt={props.alt}
      notLazy={props.notLazy}
    />
  </ResponsiveTargetLink>
);

LinkedAvatar.defaultProps = {
  photoUrl: null,
  icon: DefaultProps.icon,
  className: DefaultProps.className,
  href: DefaultProps.href,
  target: DefaultProps.target,
  onClick: DefaultProps.onClick,
  imgCdnUrl: DefaultProps.imgCdnUrl,
  alt: DefaultProps.alt,
  withBorder: DefaultProps.withBorder,
  mobileTarget: DefaultProps.mobileTarget,
};

AvatarContent.defaultProps = {
  photoUrl: LinkedAvatar.defaultProps.photoUrl,
  imgCdnUrl: DefaultProps.imgCdnUrl,
  icon: DefaultProps.icon,
  alt: DefaultProps.alt,
  notLazy: DefaultProps.notLazy,
};

/**
 * Avatar component
 */
function Avatar(props: AvatarInternalProps) {
  const {
    photo,
    photoUrl,
    imgCdnUrl,
    viewportCategory,
    icon,
    size,
    tabletSize,
    desktopSize,
    widescreenSize,
    display,
    href,
    onClick,
    alt,
  } = props;
  const avatarClass = props.withBorder ? 'ui_social_avatar' : 'ui_avatar';
  const sizeClass = props.size !== 'default' && props.size;
  const tabletSizeClass = tabletSize ? `${tabletSize}-tablet` : null;
  const desktopSizeClass = desktopSize ? `${desktopSize}-desktop` : null;
  const widescreenSizeClass = widescreenSize ? `${widescreenSize}-widescreen` : null;
  const styleClasses = classNames(
    styles.avatar,
    avatarClass,
    sizeClass,
    tabletSizeClass,
    desktopSizeClass,
    widescreenSizeClass,
    display,
    props.className,
  );

  // There is no viewport named 'widescreen' so we can't take that into consideration
  // Otherwise, for each defined viewport category, use the largest defined size that qualifies
  let viewportSize;
  if (viewportCategory === 'DESKTOP') {
    viewportSize = desktopSize || tabletSize || size;
  } else if (viewportCategory === 'TABLET') {
    viewportSize = tabletSize || size;
  } else {
    viewportSize = size;
  }

  const sizedPhotoUrl = photo ? getPhotoUrlForSize(photo, viewportSize) : photoUrl;

  if (href) {
    return (
      <LinkedAvatar
        photoUrl={sizedPhotoUrl}
        imgCdnUrl={props.imgCdnUrl}
        icon={props.icon}
        href={props.href}
        target={props.target}
        mobileTarget={props.mobileTarget}
        onClick={props.onClick}
        alt={props.alt}
        className={styleClasses}
        withBorder={props.withBorder}
        notLazy={props.notLazy}
      />
    );
  }
  return (
    <span className={styleClasses} onClick={onClick}>
      <AvatarContent
        photoUrl={sizedPhotoUrl}
        imgCdnUrl={imgCdnUrl}
        icon={icon}
        alt={alt}
        notLazy={props.notLazy}
      />
    </span>
  );
}

Avatar.defaultProps = DefaultProps;

// @deprecated: Use @ta/core-ui.avatar
export default connect<AvatarInternalProps, AvatarProps, _, _, _, _>(state => ({
  imgCdnUrl: state.meta.imageCdnUrl,
  viewportCategory: state.meta.device.viewportCategory,
}))(Avatar);
